import { get } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useContainerInfoFields } from './useContainerInfoFields'

// The same hold the stop pins on these maps already use. Without it the card fires the moment the
// cursor touches a pin, which lands on top of the pin you were reaching for — the office reported
// having to sweep off and re-approach to grab one. Matching that constant rather than picking a
// second number keeps the two previews on one map behaving alike.
const OPEN_DELAY_MS = 300

type HoverScreen = { clientX?: number; clientY?: number } | null | undefined
type HoveredContainer = { x: number; y: number; title: string; containerId: string }

// Pure resolver — maps a hovered feature + cursor position to the popover anchor,
// or null when the feature/coords are missing. Exported for tests.
export const resolveHoveredContainer = (feature: unknown, screen: HoverScreen): HoveredContainer | null => {
  const x = screen?.clientX
  const y = screen?.clientY
  if (!feature || typeof x !== 'number' || typeof y !== 'number' || !Number.isFinite(x) || !Number.isFinite(y)) {
    return null
  }
  // id: getContainers features carry the inventory id on `id`; the route geojson
  // sets it on `id` too, and the master map preserves it on `containerId`.
  const containerId = get(feature, 'properties.containerId') || get(feature, 'properties.id') || ''
  // title: prefer the configured-service name (route geojson), else the
  // inventory name / serial (getContainers).
  const title =
    get(feature, 'properties.configuredServiceContainerName') ||
    get(feature, 'properties.name') ||
    get(feature, 'properties.serialNumber') ||
    ''
  return { x, y, title, containerId }
}

// Shared container-hover wiring for every map that renders container markers
// (cluster, master, routing-edit, bulk). Tracks the hovered feature's cursor
// position + id, enriches it via useContainerInfoFields, and returns the props
// the ContainerInfoPopover needs. Pass `onHover` to the marker layer and spread
// `infoProps` onto <ContainerInfoPopover />.
export const useContainerHoverInfo = () => {
  const [hovered, setHovered] = useState<HoveredContainer | null>(null)
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingIdRef = useRef<string | null>(null)
  const openIdRef = useRef<string | null>(null)
  const latestRef = useRef<HoveredContainer | null>(null)
  const isBoxSelectingRef = useRef(false)

  const clearOpenTimer = useCallback(() => {
    if (!openTimerRef.current) return
    clearTimeout(openTimerRef.current)
    openTimerRef.current = null
  }, [])

  // Shift is the box-select drag on the bulk maps, and a card opening under the cursor mid-drag is
  // the reported complaint, so the preview stands down entirely until the key is released.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Shift') return
      isBoxSelectingRef.current = true
      clearOpenTimer()
      pendingIdRef.current = null
      openIdRef.current = null
      setHovered(null)
    }
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Shift') isBoxSelectingRef.current = false
    }
    // Focus can leave with Shift still down — dragging out of the window, or cmd-tab mid box-select.
    // The keyup then lands on another window and never arrives here, and the card stays suppressed
    // for the rest of the session; on the four maps with no box-select at all, forever after one stray
    // Shift. Blur is the only signal that the release was missed.
    const onBlur = () => {
      isBoxSelectingRef.current = false
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
      clearOpenTimer()
    }
  }, [clearOpenTimer])

  const onHover = useCallback(
    (feature: unknown, screen: HoverScreen) => {
      const next = resolveHoveredContainer(feature, screen)

      // Leaving closes at once: the card is `pointer-events: none`, so there is no trip to it to cover.
      if (!next) {
        clearOpenTimer()
        pendingIdRef.current = null
        openIdRef.current = null
        latestRef.current = null
        setHovered(null)
        return
      }

      if (isBoxSelectingRef.current) return
      latestRef.current = next

      // Open on this pin already: follow the cursor rather than serving the delay a second time.
      if (openIdRef.current === next.containerId) {
        setHovered(next)
        return
      }

      // A timer is already counting for this pin. Restarting it here would mean cursor drift inside
      // the hold — easy on a small pin — never resolves into a card at all.
      if (pendingIdRef.current === next.containerId) return

      // A second pin taking over. The deck layer fires straight from one pin to the next with no
      // gap between them, so without this the open card keeps its old container for the whole hold
      // — wrong data sitting under the cursor, worse than showing none.
      if (openIdRef.current) {
        openIdRef.current = null
        setHovered(null)
      }

      clearOpenTimer()
      const heldId = next.containerId
      pendingIdRef.current = heldId
      openTimerRef.current = setTimeout(() => {
        openTimerRef.current = null
        pendingIdRef.current = null
        // Resolve only the pin this hold counted for. Nothing reaches here on another pin today —
        // a takeover clears the timer above — but that is an argument about call order, and the
        // card is the thing that would be wrong if it ever stopped holding.
        const latest = latestRef.current
        if (latest?.containerId !== heldId) return
        openIdRef.current = heldId
        setHovered(latest)
      }, OPEN_DELAY_MS)
    },
    [clearOpenTimer]
  )

  const fields = useContainerInfoFields(hovered?.containerId)

  const infoProps = {
    open: Boolean(hovered),
    x: hovered?.x,
    y: hovered?.y,
    title: hovered?.title ?? '',
    serial: fields.serial,
    lines: fields.lines,
    chips: fields.chips,
  }

  return { onHover, infoProps }
}

export default useContainerHoverInfo
